import React, { useEffect, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, FormControl, Popover, TextField, Tooltip, Typography } from '@mui/material';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import LockIcon from '@mui/icons-material/Lock';
import M from 'messages';
import routes from 'config/route';
import classes from './styles';

const CustomDateRange = ({ field, onFilterCallback, filteredParams, isAvailable }) => {
  const memoizedFilterValue = useMemo(() => {
    if (field.isLiteral) {
      return {
        [`${field.id}_min_literal`]: filteredParams?.params?.filter?.[`${field.id}_min_literal`],
        [`${field.id}_max_literal`]: filteredParams?.params?.filter?.[`${field.id}_max_literal`],
      };
    }
    return {
      [`${field.id}_min`]: filteredParams?.params?.filter?.[`${field.id}_min`],
      [`${field.id}_max`]: filteredParams?.params?.filter?.[`${field.id}_max`],
    };
  }, [field.id, field.isLiteral, filteredParams?.params?.filter]);

  const [searchTerm, setSearchTerm] = useState(memoizedFilterValue);
  const [hasError, setHasError] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  useEffect(() => {
    setSearchTerm(memoizedFilterValue);
  }, [field, memoizedFilterValue]);

  const handleClick = event => {
    if (!isAvailable) return; // Don't open popover if not available
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setSearchTerm(memoizedFilterValue);
    setHasError(false);
    setAnchorEl(null);
  };

  const getLabel = useCallback(type => M.get(`${field.label}${type}`), [field]);
  const memoizedFromLabel = useMemo(() => getLabel('From'), [getLabel]);
  const memoizedToLabel = useMemo(() => getLabel('To'), [getLabel]);

  const getFieldId = useCallback(
    type => (field.isLiteral ? `${field.id}_${type}_literal` : `${field.id}_${type}`),
    [field],
  );
  const memoizedFieldIdFrom = useMemo(() => getFieldId('min'), [getFieldId]);
  const memoizedFieldIdTo = useMemo(() => getFieldId('max'), [getFieldId]);

  const handleApply = (newValue = null) => {
    const value = newValue || searchTerm;

    const parseUTCDate = (dateStr, isStart) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);

      // Set local time to 00:00:00 or 23:59:59.999
      if (isStart) {
        date.setHours(0, 0, 0, 0);
      } else {
        date.setHours(23, 59, 59, 999);
      }
      // Convert local time to UTC
      return date.toISOString();
    };

    const updatedValue = {
      [memoizedFieldIdFrom]: parseUTCDate(value[memoizedFieldIdFrom], true),
      [memoizedFieldIdTo]: parseUTCDate(value[memoizedFieldIdTo], false),
    };

    handleClose();
    onFilterCallback(updatedValue);
  };

  const handleClear = () => {
    const value = {
      [`${field.id}_${field.isLiteral ? 'min_literal' : 'min'}`]: '',
      [`${field.id}_${field.isLiteral ? 'max_literal' : 'max'}`]: '',
    };
    setSearchTerm(value);
    setHasError(false);
    handleApply(value);
  };

  const formatDateForInput = dateStr => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getValuePath = type => {
    const valuePath = field.isLiteral
      ? filteredParams?.params?.filter?.[`${field.id}_${type}_literal`]
      : filteredParams?.params?.filter?.[`${field.id}_${type}`];
    return valuePath;
  };

  const getValue = () => {
    const minValue = getValuePath('min');
    const maxValue = getValuePath('max');

    if (minValue === '' && maxValue === '') {
      return '';
    }

    if (minValue || maxValue) {
      let value = '';
      if (minValue) {
        value = `${M.get(field.label)} From: ${formatDateForInput(minValue)}`;
      } else {
        value = M.get(field.label);
      }
      if (maxValue) {
        value = `${value} To: ${formatDateForInput(maxValue)}`;
      }
      return value;
    }
    return '';
  };

  const validateRange = (newValue, fieldKey) => {
    if (
      (searchTerm[memoizedFieldIdTo] &&
        fieldKey === memoizedFieldIdFrom &&
        new Date(newValue) > new Date(searchTerm[memoizedFieldIdTo])) ||
      (newValue &&
        searchTerm[memoizedFieldIdFrom] &&
        fieldKey === memoizedFieldIdTo &&
        new Date(newValue) < new Date(searchTerm[memoizedFieldIdFrom]))
    ) {
      setHasError(true);
      return;
    }
    setHasError(false);
  };

  const handleRangeInputChange = (newValue, fieldKey) => {
    setSearchTerm({ ...searchTerm, [fieldKey]: newValue });
    validateRange(newValue, fieldKey);
  };

  const renderLockIcon = () => (
    <Tooltip
      PopperProps={{
        sx: {
          '& .MuiTooltip-tooltip': classes.tooltip,
        },
      }}
      title={
        <Typography sx={classes.noAccess}>
          {M.get('subscriptions.noAccess1')}
          &nbsp;
          <a style={classes.link} href={`${routes.subscriptions.path}?type=standard`} rel="noopener noreferrer">
            {M.get('subscriptions.upgrade')}
          </a>
          &nbsp;
          {M.get('subscriptions.noAccess2')}
        </Typography>
      }
      placement="top">
      <LockIcon />
    </Tooltip>
  );

  const getEndAdornment = () => {
    if (!isAvailable) {
      return <Box sx={classes.lockIcon}>{renderLockIcon()}</Box>;
    }
    return <Box sx={classes.arrowIcon}>{open ? <ArrowDropUp /> : <ArrowDropDown />}</Box>;
  };

  return (
    <Box sx={classes.box}>
      <FormControl margin="dense" fullWidth variant="outlined">
        <Box
          sx={{
            ...classes.rangeInput,
            ...(getValue() && classes[field.selectedClassName]),
            ...(!isAvailable && classes.disabledBox),
          }}
          onClick={handleClick}
          onKeyDown={handleClick}
          role="button"
          tabIndex={0}>
          <Box sx={classes.valueText}>
            {getValue() ? (
              <Box component="span">{getValue()}</Box>
            ) : (
              <Box sx={classes.placeholder}>{M.get(`${field.label}Range`)}</Box>
            )}
          </Box>
          {getEndAdornment()}
        </Box>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: classes.paper } }}>
          <Box sx={classes.popoverContainer}>
            <FormControl fullWidth variant="outlined">
              <TextField
                variant="standard"
                sx={{
                  ...classes.customTextField,
                  '& .MuiInputLabel-root': { fontSize: 14 },
                }}
                size="small"
                placeholder={memoizedFromLabel}
                id={memoizedFieldIdFrom}
                value={formatDateForInput(searchTerm?.[memoizedFieldIdFrom]) || ''}
                name={memoizedFieldIdFrom}
                label={memoizedFromLabel}
                type="date"
                slotProps={{
                  inputLabel: { shrink: true },
                }}
                onChange={event => {
                  const { value } = event.target;
                  handleRangeInputChange(value, memoizedFieldIdFrom);
                }}
              />
              <TextField
                variant="standard"
                sx={{
                  ...classes.customTextField,
                  '& .MuiInputLabel-root': { fontSize: 14 },
                }}
                size="small"
                placeholder={memoizedToLabel}
                id={memoizedFieldIdTo}
                value={formatDateForInput(searchTerm?.[memoizedFieldIdTo]) || ''}
                name={memoizedFieldIdTo}
                label={memoizedToLabel}
                type="date"
                slotProps={{
                  inputLabel: { shrink: true },
                }}
                onChange={event => {
                  const { value } = event.target;
                  handleRangeInputChange(value, memoizedFieldIdTo);
                }}
                error={hasError}
              />
              <Box sx={classes.actions}>
                <Button sx={classes.capitalize} onClick={handleClear}>
                  {M.get('actions.clear')}
                </Button>
                <Button
                  disabled={hasError}
                  sx={{ ...classes.primaryIconButton, ...classes.capitalize }}
                  onClick={() => handleApply()}
                  variant="contained"
                  color="primary">
                  {M.get('actions.apply')}
                </Button>
              </Box>
            </FormControl>
          </Box>
        </Popover>
      </FormControl>
    </Box>
  );
};

CustomDateRange.propTypes = {
  onFilterCallback: PropTypes.func.isRequired,
  field: PropTypes.instanceOf(Object).isRequired,
  filteredParams: PropTypes.instanceOf(Object).isRequired,
  isAvailable: PropTypes.bool.isRequired,
};

export default CustomDateRange;
